<template>
  <div>
    <CRow
      class="form-group"
    >
      <CCol
        sm="3"
        class="text-right pr-4"
      >
        Dynamic Number
      </CCol>
      <CCol sm="3">
        <CSwitch
          shape="pill"
          :checked.sync="
            programmaticTargetCopy.isNumberDynamic
          "
          color="primary"
          @update:checked="$emit('update', programmaticTargetCopy)"
        />
      </CCol>
    </CRow>

    <div v-if="!programmaticTargetCopy.isNumberDynamic">
      <CRow class="form-group">
        <CCol
          sm="3"
          class="text-right pr-4"
        >
          Type
        </CCol>
        <CInputRadioGroup
          class="col-sm-9"
          add-label-classes="text-right pr-4"
          add-input-classes="w-25"
          :options="['Number', 'SIP']"
          :inline="true"
          :custom="true"
          :checked.sync="programmaticTargetCopy.destinationType"
          label="Routing"
          @update:checked="onDestinationTypeChanged"
        />
      </CRow>

      <CInput
        v-if="programmaticTargetCopy.destinationType === 'Number'"
        v-model="$v.programmaticTargetCopy.number.$model"
        type="string"
        label="Number"
        add-label-classes="text-right pr-4"
        add-input-classes="w-25"
        name="Number"
        :is-valid="
          checkIfValid(['programmaticTargetCopy', 'number'])
        "
        placeholder="Number"
        horizontal
        invalid-feedback="Phone number cannot be blank"
        @input="$emit('update', programmaticTargetCopy)"
      />

      <div v-if="programmaticTargetCopy.destinationType === 'SIP'">
        <CInput
          v-model="$v.programmaticTargetCopy.sipNumber.endpoint.$model"
          type="string"
          label="SIP Endpoint"
          add-label-classes="text-right pr-4"
          add-input-classes="w-25"
          name="SIP Endpoint"
          :is-valid="
            checkIfValid(['programmaticTargetCopy', 'sipNumber', 'endpoint'])
          "
          placeholder="SIP URL"
          horizontal
          invalid-feedback="SIP Endpoint must be a valid URL"
          @input="$emit('update', programmaticTargetCopy)"
        />
        <CInput
          v-model="$v.programmaticTargetCopy.sipNumber.username.$model"
          type="string"
          label="SIP Username"
          add-label-classes="text-right pr-4"
          add-input-classes="w-25"
          name="SIP Username"
          placeholder="SIP Username"
          :is-valid="
            checkIfValid(['programmaticTargetCopy', 'sipNumber', 'username'])
          "
          invalid-feedback="username cannot be blank"
          horizontal
          @input="$emit('update', programmaticTargetCopy)"
        />
        <CInput
          v-model="$v.programmaticTargetCopy.sipNumber.password.$model"
          :type="showSIPPassword ? 'text' : 'password'"
          label="SIP Password"
          add-label-classes="text-right pr-4"
          add-input-classes="w-25 col-sm-3"
          name="SIP Password"
          placeholder="SIP Password"
          :is-valid="
            checkIfValid(['programmaticTargetCopy', 'sipNumber', 'password'])
          "
          invalid-feedback="password cannot be blank"
          horizontal
          @input="$emit('update', programmaticTargetCopy)"
        >
          <template #append-content>
            <span
              :style="{ cursor: 'pointer' }"
              @click="toggleSIPPasswordVisibility"
            >
              {{ showSIPPassword ? 'hide' : 'show' }}
            </span>
          </template>
        </CInput>
      </div>
    </div>


    <div id="section-bid-settings">
      Bid Settings
      <hr>

      <CRow class="form-group">
        <CCol
          sm="3"
          class="text-right pr-4"
        >
          Bid Type
        </CCol>

        <CCol sm="9">
          <CInputRadioGroup
            add-label-classes="text-right pr-4"
            add-input-classes="w-25"
            :options="bidTypeOptions"
            :custom="true"
            :inline="true"
            :checked.sync="programmaticTargetCopy.bidSettings.bidType"
            @update:checked="$emit('update', programmaticTargetCopy)"
          />
          <small
            v-if="isCpaCategory"
            class="text-muted d-block mt-1"
          >
            Dynamic bid is not available for CPA categories
          </small>
        </CCol>
      </CRow>

      <CInput
        v-if="!programmaticTargetCopy.bidSettings.isBidDynamic || isCpaCategory"
        v-model="$v.programmaticTargetCopy.bidSettings.staticBidAmount.$model"
        type="number"
        label="Static Bid"
        add-label-classes="text-right pr-4"
        add-input-classes="w-25"
        description="amount in USD"
        name="Static Bid"
        placeholder=""
        horizontal
        min="0"
        :is-valid="
          checkIfValid([
            'programmaticTargetCopy',
            'bidSettings',
            'staticBidAmount'
          ])
        "
        :invalid-feedback="isCpaCategory ? 'Static bid is required for CPA categories' : 'Invalid bid amount'"
        @input="$emit('update', programmaticTargetCopy)"
      />

      <CInput
        v-model="$v.programmaticTargetCopy.bidSettings.bidOnFailure.$model"
        type="number"
        label="Failure Bid"
        add-label-classes="text-right pr-4"
        add-input-classes="w-25"
        name="Failure Bid"
        description="amount in USD"
        placeholder=""
        horizontal
        min="0"
        :is-valid="
          checkIfValid([
            'programmaticTargetCopy',
            'bidSettings',
            'bidOnFailure'
          ])
        "
        invalid-feedback="Invalid failure bid amount"
        @input="$emit('update', programmaticTargetCopy)"
      />

      <template v-if="!isCpaCategory">
        <CRow class="form-group">
          <CCol
            sm="3"
            class="text-right pr-4"
          >
            Conversion Duration
          </CCol>

          <CInputRadioGroup
            class="col-sm-9"
            add-label-classes="text-right pr-4"
            add-input-classes="w-25"
            :options="['Dynamic', 'Static']"
            :custom="true"
            :inline="true"
            :checked.sync="
              programmaticTargetCopy.bidSettings.conversionDurationType
            "
            @update:checked="$emit('update', programmaticTargetCopy)"
          />
        </CRow>

        <CInput
          v-if="!programmaticTargetCopy.bidSettings.conversionCallLengthDynamic"
          v-model="
            $v.programmaticTargetCopy.bidSettings.conversionCallLengthInSeconds
              .$model
          "
          type="number"
          label="Call Length"
          add-label-classes="text-right pr-4"
          add-input-classes="w-25"
          name="Call Length"
          placeholder=""
          horizontal
          description="call duration in seconds"
          min="0"
          :is-valid="
            checkIfValid([
              'programmaticTargetCopy',
              'bidSettings',
              'conversionCallLengthInSeconds'
            ])
          "
          invalid-feedback="Invalid call length"
          @input="$emit('update', programmaticTargetCopy)"
        />

        <CRow
          v-if="programmaticTargetCopy.bidSettings.conversionCallLengthDynamic"
          class="form-group"
        >
          <CCol
            sm="3"
            class="text-right pr-4"
          >
            Duration Limit
          </CCol>
          <CCol sm="9">
            <CSwitch
              shape="pill"
              color="primary"
              :checked.sync="programmaticTargetCopy.bidSettings.enableDurationLimit"
              @update:checked="onDurationLimitToggled"
            />
          </CCol>
        </CRow>

        <CInput
          v-if="programmaticTargetCopy.bidSettings.conversionCallLengthDynamic && programmaticTargetCopy.bidSettings.enableDurationLimit"
          v-model="
            $v.programmaticTargetCopy.bidSettings.maxConversionCallDurationSeconds
              .$model
          "
          type="number"
          label=" "
          add-label-classes="text-right pr-4"
          add-input-classes="w-25"
          name="Duration Limit"
          description="seconds"
          placeholder=""
          horizontal
          min="0"
          :is-valid="
            checkIfValid([
              'programmaticTargetCopy',
              'bidSettings',
              'maxConversionCallDurationSeconds'
            ])
          "
          invalid-feedback="Invalid value for duration"
          @input="$emit('update', programmaticTargetCopy)"
        />
      </template>
    </div>

    <div id="section-request-settings">
      Request Settings
      <hr>

      <CInput
        v-model="$v.programmaticTargetCopy.url.$model"
        type="string"
        label="URL"
        add-label-classes="text-right pr-4"
        add-input-classes="w-50"
        name="URL"
        placeholder=""
        :is-valid="checkIfValid(['programmaticTargetCopy', 'url'])"
        horizontal
        invalid-feedback="Invalid URL"
        @input="$emit('update', programmaticTargetCopy)"
      />

      <CSelect
        id="buyer-campaign__destination__request-settings__request-type"
        :value.sync="$v.programmaticTargetCopy.method.$model"
        add-label-classes="text-right pr-4"
        add-input-classes="w-25"
        data-cy="buyer-campaign__destination__request-settings__request-type"
        label="Method"
        horizontal
        :options="requestTypes"
        placeholder="Please select"
        :is-valid="checkIfValid(['programmaticTargetCopy', 'method'])"
        invalid-feedback="Please select method"
        @update:value="$emit('update', programmaticTargetCopy)"
      />

      <div v-if="canRequestHaveBody">
        <CInput
          v-model="$v.programmaticTargetCopy.contentType.$model"
          type="text"
          label="Content Type"
          add-label-classes="text-right pr-4"
          add-input-classes="w-50"
          name="Content Type"
          placeholder=""
          horizontal
          :is-valid="checkIfValid(['programmaticTargetCopy', 'contentType'])"
          invalid-feedback="Content Type cannot be blank"
          @input="$emit('update', programmaticTargetCopy)"
        />

        <CTextarea
          v-model="$v.programmaticTargetCopy.body.$model"
          type="text"
          rows="7"
          label="Request Body"
          add-label-classes="text-right pr-4"
          add-input-classes="w-75"
          name="Request Body"
          placeholder=""
          horizontal
          :is-valid="checkIfValid(['programmaticTargetCopy', 'body'])"
          invalid-feedback="Request Body cannot be blank"
          @input="$emit('update', programmaticTargetCopy)"
        />
      </div>

      <CRow class="form-group">
        <CCol
          sm="3"
          class="text-right pr-4"
        >
          Headers
        </CCol>
        <CCol sm="9">
          <CButton
            v-if="
              programmaticTargetCopy &&
                programmaticTargetCopy.headers &&
                programmaticTargetCopy.headers.length === 0
            "
            color="dark"
            size="sm"
            @click="addRequestHeader"
          >
            Add Headers
          </CButton>
          <div
            v-if="
              programmaticTargetCopy &&
                programmaticTargetCopy.headers &&
                programmaticTargetCopy.headers.length > 0
            "
          >
            <CRow
              v-for="(header, index) in programmaticTargetCopy.headers"
              :key="`custom-header-${index}`"
              class="pl-2"
            >
              <CInput
                v-model="header.key"
                @input="$emit('update', programmaticTargetCopy)"
              />
              <CInput
                v-model="header.value"
                class="ml-2"
                @input="$emit('update', programmaticTargetCopy)"
              />
              <span @click="addRequestHeader">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2"
                  name="cil-plus"
                />
              </span>
              <span @click="removeRequestHeader(index)">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2"
                  name="cil-minus"
                />
              </span>
            </CRow>
          </div>
        </CCol>
      </CRow>
    </div>       

    <div
      v-if="programmaticTargetCopy.isNumberDynamic"
      id="section-dynamic-number-parsing"
    >
      Dynamic Number Parsing
      <hr>

      <ProgrammaticTargetParsersList
        :submitted="formSubmitted"
        :parsers="programmaticTargetCopy.dynamicDialSettingParser"
        @update="updateDynamicNumberParser"
      />
    </div>

    <div
      v-if="programmaticTargetCopy.bidSettings.isBidDynamic"
      id="section-bid-parsing"
    >
      Bid Parsing
      <hr>

      <ProgrammaticTargetParsersList
        :submitted="formSubmitted"
        :parsers="programmaticTargetCopy.dynamicBidAmountParser"
        @update="updateDynamicBidAmountParser"
      />
    </div>

    <div id="section-call-acceptance-parsing">
      Call Acceptance Parsing
      <hr>

      <ProgrammaticTargetParsersList
        :submitted="formSubmitted"
        :parsers="programmaticTargetCopy.shouldAcceptCallParser"
        @update="updateDynamicCallAcceptParser"
      />
    </div>

    <div
      v-if="programmaticTargetCopy.bidSettings.conversionCallLengthDynamic && !isCpaCategory"
      id="section-dynamic-duration-parsing"
    >
      Dynamic Duration Parsing
      <hr>

      <ProgrammaticTargetParsersList
        :submitted="formSubmitted"
        :parsers="programmaticTargetCopy.dynamicCallLengthSettingParser"
        @update="updateDynamicDurationParser"
      />
    </div>

    <div id="section-confirmation-request-settings">
      Confirmation Request Settings
    </div>
    <hr>

    <CRow class="form-group">
      <CCol
        sm="3"
        col="12"
        class="text-sm-right pr-4"
      >
        Two-step RTT 
      </CCol>
      <CCol
        sm="3"
        col="12"
      >
        <CSwitch
          shape="pill"
          :checked.sync="programmaticTargetCopy.bidAcceptanceSettings.enabled"
          color="primary"
          @update:checked="$emit('update', programmaticTargetCopy)"         
        />
      </CCol>
    </CRow>
    <div 
      v-if="programmaticTargetCopy && 
        programmaticTargetCopy.bidAcceptanceSettings && 
        programmaticTargetCopy.bidAcceptanceSettings.enabled"
    >
      <CInput        
        v-model="$v.programmaticTargetCopy.bidAcceptanceSettings.url.$model"
        type="string"
        label="Ping URL"
        add-label-classes="text-right pr-4"
        add-input-classes="w-50"
        name="Ping URL"
        placeholder=""      
        :is-valid="checkIfValid(['programmaticTargetCopy', 'bidAcceptanceSettings', 'url'])"
        horizontal
        invalid-feedback="Invalid URL"       
        @input="$emit('update', programmaticTargetCopy)"
      />

      <CSelect
        id="buyer-campaign__destination__request-settings__ping-request-type"
        :value.sync="$v.programmaticTargetCopy.bidAcceptanceSettings.method.$model"
        add-label-classes="text-right pr-4"
        add-input-classes="w-25"
        data-cy="buyer-campaign__destination__request-settings__ping-request-type"
        label="Method"
        horizontal
        :options="requestTypes"
        placeholder="Please select"
        :is-valid="checkIfValid(['programmaticTargetCopy', 'bidAcceptanceSettings', 'method'])"
        invalid-feedback="Please select method"   
        @update:value="$emit('update', programmaticTargetCopy)"
      />

      <div
        v-if="canPingRequestHaveBody"
      >
        <CInput
          v-model="$v.programmaticTargetCopy.bidAcceptanceSettings.contentType.$model"
          type="text"
          label="Content Type"
          add-label-classes="text-right pr-4"
          add-input-classes="w-50"
          name="Request Content Type"
          placeholder=""
          horizontal
          :is-valid="checkIfValid(['programmaticTargetCopy', 'bidAcceptanceSettings', 'contentType'])"
          invalid-feedback="Content Type cannot be blank"
          @input="$emit('update', programmaticTargetCopy)"
        />

        <CTextarea
          v-model="$v.programmaticTargetCopy.bidAcceptanceSettings.body.$model"
          type="text"
          rows="7"
          label="Request Body"
          add-label-classes="text-right pr-4"
          add-input-classes="w-75"
          name="Request Body"
          placeholder=""
          horizontal
          :is-valid="checkIfValid(['programmaticTargetCopy', 'bidAcceptanceSettings', 'body'])"
          invalid-feedback="Request Body cannot be blank"
          @input="$emit('update', programmaticTargetCopy)"
        />
      </div>

      <CRow 
        class="form-group"
      >
        <CCol
          sm="3"
          class="text-right pr-4"
        >
          Request Headers
        </CCol>
        <CCol sm="9">
          <CButton
            v-if="
              programmaticTargetCopy &&
                programmaticTargetCopy.bidAcceptanceSettings &&
                programmaticTargetCopy.bidAcceptanceSettings.headers &&
                programmaticTargetCopy.bidAcceptanceSettings.headers.length === 0
            "
            color="dark"
            size="sm"
            @click="addPingRequestHeader"
          >
            Add Headers
          </CButton>
          <div
            v-if="
              programmaticTargetCopy &&
                programmaticTargetCopy.bidAcceptanceSettings &&
                programmaticTargetCopy.bidAcceptanceSettings.headers &&
                programmaticTargetCopy.bidAcceptanceSettings.headers.length > 0
            "
          >
            <CRow
              v-for="(header, index) in programmaticTargetCopy.bidAcceptanceSettings.headers"
              :key="`custom-ping-header-${index}`"
              class="pl-2"
            >
              <CInput
                v-model="header.key"
                @input="$emit('update', programmaticTargetCopy)"
              />
              <CInput
                v-model="header.value"
                class="ml-2"
                @input="$emit('update', programmaticTargetCopy)"
              />
              <span @click="addPingRequestHeader">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2"
                  name="cil-plus"
                />
              </span>
              <span @click="removePingRequestHeader(index)">
                <CIcon
                  style="cursor: pointer;"
                  class="ml-2 my-2"
                  name="cil-minus"
                />
              </span>
            </CRow>
          </div>
        </CCol>
      </CRow>
    
      <CInput
        v-model="$v.programmaticTargetCopy.bidAcceptanceSettings.pingTimeoutInMs.$model"
        type="number"
        label="Ping timeout in milliseconds"
        add-label-classes="text-right pr-4"
        add-input-classes="w-25"
        name="PingTimeoutInMs"
        placeholder="e.g. 5000"
        horizontal
        min="1"
        invalid-feedback="Ping timeout must be greater than 0"
        :is-valid="checkIfValid(['programmaticTargetCopy', 'bidAcceptanceSettings', 'pingTimeoutInMs'])"
        @input="$emit('update', programmaticTargetCopy)"
      />

      <CRow class="form-group">
        <CCol
          v-c-tooltip="'If enabled, a second confirmation request will be sent to accept the bid regardless if a number was already provided in the first request'" 
          sm="3"
          col="12"
          class="text-sm-right pr-4"
        >
          Confirmation Request Required
        </CCol>
        <CCol
          sm="3"
          col="12"
        >
          <CSwitch
            shape="pill"
            :checked.sync="programmaticTargetCopy.bidAcceptanceSettings.required"
            color="primary"
            @update:checked="$emit('update', programmaticTargetCopy)"
          />
        </CCol>
      </CRow>

      <CRow class="form-group">
        <CCol
          v-c-tooltip="'Is it necessary to parse the Bid ID from the first ping to do the confirmation request? When enabled, provide parsing expression below.'" 
          sm="3"
          col="12"
          class="text-sm-right pr-4"
        >
          Bid ID Required
        </CCol>
        <CCol
          sm="3"
          col="12"
        >
          <CSwitch
            shape="pill"
            :checked.sync="programmaticTargetCopy.bidAcceptanceSettings.bidIdRequired"
            color="primary"
            @update:checked="$emit('update', programmaticTargetCopy)"
          />
        </CCol>
      </CRow>

      <CRow 
        v-if="programmaticTargetCopy.isNumberDynamic"
        class="form-group"
      >
        <CCol
          v-c-tooltip="'If this is enabled the number used will be parsed from the confirmation request, else it will use the number parsed on the first ping request. When enabled, provide parsing expression below.'" 
          sm="3"
          col="12"          
          class="text-sm-right pr-4"          
        >
          Use Number from Confirmation Request
        </CCol>
        <CCol
          sm="3"
          col="12"
        >
          <CSwitch
            shape="pill"
            :checked.sync="programmaticTargetCopy.bidAcceptanceSettings.numberRequired"
            color="primary"
            @update:checked="$emit('update', programmaticTargetCopy)"
          />
        </CCol>
      </CRow>
    </div>

    <div
      v-if="programmaticTargetCopy.bidAcceptanceSettings.enabled && programmaticTargetCopy.bidAcceptanceSettings.bidIdRequired"
      id="section-bid-id-parsing"
    >
      Bid ID Parsing (Two-step RTT)
      <hr>   

      <ProgrammaticTargetParsersList
        :submitted="formSubmitted"
        :parsers="programmaticTargetCopy.bidAcceptanceSettings.bidIdParseSettings"
        @update="updateDynamicBidIdParser"
      />
    </div>

    <div
      v-if="programmaticTargetCopy.bidAcceptanceSettings.enabled && programmaticTargetCopy.bidAcceptanceSettings.numberRequired"
      id="section-number-sip-parsing"
    >
      Number / SIP Parsing (Two-step RTT)
      <hr>

      <ProgrammaticTargetParsersList
        :submitted="formSubmitted"
        :parsers="programmaticTargetCopy.bidAcceptanceSettings.parseSettings 
          ? programmaticTargetCopy.bidAcceptanceSettings.parseSettings.dynamicDial 
          : []"
        @update="updateDynamicNumberSipParser"
      />
    </div>
  </div>
</template>

<script>
import ProgrammaticTargetParsersList from './ProgrammaticTargetParsersList.vue'
import customURLValidator  from '@/validators/customURLValidator'
import { required, minValue, requiredIf } from 'vuelidate/lib/validators'
import formMixins from '@/mixins/form-mixins'

export default {
  name: 'ProgrammaticTargetForm',
  components: {
    ProgrammaticTargetParsersList
  },
  mixins: [formMixins],
  props: {
    formSubmitted: {
      type: Boolean,
      default: false
    },
    programmaticTarget: {
      type: Object,
      default: () => {}
    },
    isCpaCategory: {
      type: Boolean,
      default: false
    }
  },

  validations() {
    return {
      programmaticTargetCopy: {
        bidSettings: {
          staticBidAmount: {
            requiredIf: requiredIf(() => {
              return !this.programmaticTargetCopy.bidSettings.isBidDynamic || this.isCpaCategory
            }),
            minValue: minValue(0)
          },
          bidOnFailure: {
            required,
            minValue: minValue(0)
          },
          conversionCallLengthInSeconds: {
            requiredIf: requiredIf(() => {
              return !this.isCpaCategory && !this.programmaticTargetCopy.bidSettings.conversionCallLengthDynamic
            }),
            minValue: minValue(0)
          },
          maxConversionCallDurationSeconds: {
            requiredIf: requiredIf(() => {
              return this.programmaticTargetCopy.bidSettings.conversionCallLengthDynamic && this.programmaticTargetCopy.bidSettings.enableDurationLimit
            }),
            minValue: minValue(0)
          }
        },
        url: {
          required,
          customURLValidator
        },
        method: { required },
        contentType: {
          requiredIf: requiredIf(() => this.canRequestHaveBody)
        },
        body: {
          requiredIf: requiredIf(() => this.canRequestHaveBody)
        },
        number: {
          requiredIf: requiredIf(() => {
            return !this.programmaticTargetCopy.isNumberDynamic && this.programmaticTargetCopy.destinationType === 'Number'
          })
        },
        sipNumber: {
          endpoint: {
            requiredIf: requiredIf(() => {
              return !this.programmaticTargetCopy.isNumberDynamic && this.programmaticTargetCopy.destinationType === 'SIP'
            }),
            customURLValidator: (value) => {
              if (this.programmaticTargetCopy.isNumberDynamic || this.programmaticTargetCopy.destinationType !== 'SIP') {
                return true
              }

              return customURLValidator(value)
            }
          },
          username: {
            requiredIf: requiredIf(() => {
              return !this.programmaticTargetCopy.isNumberDynamic && this.programmaticTargetCopy.destinationType === 'SIP' && !!this.programmaticTargetCopy.sipNumber?.password
            }),
          },
          password: {
            requiredIf: requiredIf(() => {
              return !this.programmaticTargetCopy.isNumberDynamic && this.programmaticTargetCopy.destinationType === 'SIP' && !!this.programmaticTargetCopy.sipNumber?.username
            }),
          }
        },
        bidAcceptanceSettings : {
          url: {
            requiredIf: requiredIf(() => {
              return this.programmaticTargetCopy.bidAcceptanceSettings.enabled
            }),
            customURLValidator: (value) => {
              if (!this.programmaticTargetCopy.bidAcceptanceSettings.enabled) {
                return true
              }

              return customURLValidator(value)
            }
          },
          method: { 
            requiredIf: requiredIf(() => {
              return this.programmaticTargetCopy.bidAcceptanceSettings.enabled
            })
          },
          pingTimeoutInMs: {
            requiredIf: requiredIf(() => {
              return this.programmaticTargetCopy.bidAcceptanceSettings.enabled
            }),
            minValue: minValue(1)
          },
          contentType: {
            requiredIf: requiredIf(() => {
              return this.programmaticTargetCopy.bidAcceptanceSettings.enabled && this.canPingRequestHaveBody
            })
          },
          body: {
            requiredIf: requiredIf(() => {
              return this.programmaticTargetCopy.bidAcceptanceSettings.enabled && this.canPingRequestHaveBody
            })
          }
        }
      }
    }
  },

  data() {
    return {
      showSIPPassword: false,
      requestTypes: [
        { value: 'get', label: 'GET' },
        { value: 'post', label: 'POST' },
        { value: 'put', label: 'PUT' },
        { value: 'patch', label: 'PATCH' },
        { value: 'delete', label: 'DELETE' }
      ],
      programmaticTargetCopy: {
        bidSettings: {},
        bidAcceptanceSettings: {}
      }
    }
  },
  computed: {
    bidTypeOptions() {
      return [
        { value: 'Dynamic', label: 'Dynamic', props: { disabled: this.isCpaCategory } },
        { value: 'Static', label: 'Static' }
      ]
    },

    canRequestHaveBody() {
      const methodsThatCanHaveBody = new Set(['post', 'put', 'patch', 'delete'])
      return methodsThatCanHaveBody.has(this.programmaticTargetCopy.method)
    },

    canPingRequestHaveBody() {
      const methodsThatCanHaveBody = new Set(['post', 'put', 'patch', 'delete'])
      return methodsThatCanHaveBody.has(this.programmaticTargetCopy.bidAcceptanceSettings.method)
    }
  },
  watch: {
    formSubmitted: {
      immediate: true,
      handler(newValue) {
        this.submitted = newValue
      }
    },
    programmaticTarget: {
      immediate: true,
      handler(newValue) {
        const bidType = this.isCpaCategory ? 'Static' : (newValue.bidSettings.isBidDynamic ? 'Dynamic' : 'Static')
        this.programmaticTargetCopy = {
          ...newValue,
          bidSettings: {
            ...newValue.bidSettings,
            bidType,
            isBidDynamic: bidType === 'Dynamic',
            conversionDurationType: newValue.bidSettings
              .conversionCallLengthDynamic
              ? 'Dynamic'
              : 'Static',
            enableDurationLimit: newValue.bidSettings.enableDurationLimit !== undefined
              ? newValue.bidSettings.enableDurationLimit
              : newValue.bidSettings.maxConversionCallDurationSeconds != null
          },
          bidAcceptanceSettings: {
            ...newValue.bidAcceptanceSettings,
            headers: this.normalizeHeaders(newValue.bidAcceptanceSettings?.headers)
          },
          headers: this.normalizeHeaders(newValue.headers)
        }
      }
    },
    isCpaCategory: {
      handler(newValue) {
        if (newValue && this.programmaticTargetCopy.bidSettings.bidType === 'Dynamic') {
          this.programmaticTargetCopy.bidSettings.bidType = 'Static'
          this.programmaticTargetCopy.bidSettings.isBidDynamic = false
          this.$emit('update', this.programmaticTargetCopy)
        }
      }
    }
  },
  methods: {
    onDestinationTypeChanged() {
      if (this.programmaticTargetCopy.destinationType === 'SIP') {
        this.programmaticTargetCopy.sipNumber = {}
      }

      this.$emit('update', this.programmaticTargetCopy)
    },    
    addRequestHeader() {
      this.programmaticTargetCopy.headers.push({
        key: '',
        value: ''
      })
    },
    addPingRequestHeader() {     
      this.programmaticTargetCopy.bidAcceptanceSettings.headers.push({
        key: '',
        value: ''
      })      
    },
    removeRequestHeader(index) {
      this.programmaticTargetCopy.headers.splice(index, 1)
      this.$emit('update', this.programmaticTargetCopy)
    },
    removePingRequestHeader(index) {
      this.programmaticTargetCopy.bidAcceptanceSettings.headers.splice(index, 1)
      this.$emit('update', this.programmaticTargetCopy)
    },
    updateDynamicNumberParser(parsers) {
      this.programmaticTargetCopy.dynamicDialSettingParser = parsers
      this.$emit('update', this.programmaticTargetCopy)
    },
    updateDynamicBidAmountParser(parsers) {
      this.programmaticTargetCopy.dynamicBidAmountParser = parsers
      this.$emit('update', this.programmaticTargetCopy)
    },
    updateDynamicCallAcceptParser(parsers) {
      this.programmaticTargetCopy.shouldAcceptCallParser = parsers
      this.$emit('update', this.programmaticTargetCopy)
    },
    updateDynamicDurationParser(parsers) {
      this.programmaticTargetCopy.dynamicCallLengthSettingParser = parsers
      this.$emit('update', this.programmaticTargetCopy)
    },
    updateDynamicBidIdParser(parsers) {
      this.programmaticTargetCopy.bidAcceptanceSettings.bidIdParseSettings = parsers
      this.$emit('update', this.programmaticTargetCopy)
    },
    updateDynamicNumberSipParser(parsers) {
      this.programmaticTargetCopy.bidAcceptanceSettings.parseSettings = {dynamicDial: parsers }
      this.$emit('update', this.programmaticTargetCopy)
    },
    toggleSIPPasswordVisibility() {
      this.showSIPPassword = !this.showSIPPassword
    },
    onDurationLimitToggled() {
      if (!this.programmaticTargetCopy.bidSettings.enableDurationLimit) {
        this.programmaticTargetCopy.bidSettings.maxConversionCallDurationSeconds = null
      }

      this.$emit('update', this.programmaticTargetCopy)
    },
    normalizeHeaders(headers) {
      if (!headers) {
        return [] 
      }

      if (Array.isArray(headers)) {
        return headers 
      }

      return Object.entries(headers).map(entry => ({
        key: entry[0],
        value: entry[1]
      }))
    }
  }
}
</script>

<style lang="scss" scoped></style>
